import { lazy, Suspense, useEffect } from "react";
import { X as CloseIcon } from "lucide-react";

import {
  Box,
  Button,
  DialogContent,
  Grid,
  IconButton,
  Skeleton,
  Stack,
} from "@mui/material";
import {
  IgBasePrimitives,
  IgGreenPrimitives,
} from "@intelligems/ig-design-tokens";
import { IgChip, IgTypography } from "@intelligems/ig-theme";

import type {
  GatedModuleButton,
  GateModuleDetails,
} from "./PricingGatedModal.types";
import { GateTrialExclusionChip } from "./GateTrialExclusionChip";
import { useIsInView } from "@src/hooks/is-in-view";
import {
  type GatedModuleOrderVolumeTier,
  type GatedModuleUserState,
  mpGatedModuleVideoStarted,
  mpSmartPricingVideoStarted,
} from "@src/utils/mixpanel";
import {
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
} from "@src/constants/billing";

const MuxPlayer = lazy(async () => import("@mux/mux-player-react"));

// The demo videos are 16:9. Reserving the frame up front keeps the dialog from
// resizing when the lazily-loaded player replaces the placeholder.
const PLAYER_FRAME_SX = {
  position: "relative",
  width: "100%",
  maxWidth: "500px",
  aspectRatio: "16 / 9",
  borderRadius: "6px",
  overflow: "hidden",
};

const PLAYER_FILL_SX = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
};

const PLAYER_FILL_STYLE = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
} as const;

const sharedChipSx = {
  fontSize: "10px",
  fontWeight: "700",
  letterSpacing: "5%",
  p: "4px 10px",
  textTransform: "uppercase",
};

type BillingModuleGateDemoProps = {
  buttons: GatedModuleButton[];
  gateModuleDetails: GateModuleDetails;
  moduleId: string;
  onBack: () => void;
  orderVolumeTier: GatedModuleOrderVolumeTier;
  userState: GatedModuleUserState;
  videoPlaybackId: string;
};

export const BillingModuleGateDemo = ({
  buttons,
  gateModuleDetails,
  moduleId,
  onBack,
  orderVolumeTier,
  userState,
  videoPlaybackId,
}: BillingModuleGateDemoProps) => {
  const { description, discount, isExcludedFromTrial, name, title } =
    gateModuleDetails;
  const { ref: viewRef, isInView } = useIsInView();

  useEffect(() => {
    if (!isInView) {
      return;
    }

    if (
      moduleId === SMART_PRICING_LIMITED_ID ||
      moduleId === SMART_PRICING_UNLIMITED_ID
    ) {
      mpSmartPricingVideoStarted({
        orderVolumeTier,
        userState,
      });
    } else {
      mpGatedModuleVideoStarted({
        moduleName: name,
        source: "modal",
        userState,
      });
    }
  }, [isInView, moduleId, orderVolumeTier, userState]);

  return (
    <>
      <IconButton
        aria-label="close"
        onClick={onBack}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
          zIndex: 1,
        })}
      >
        <CloseIcon size={24} />
      </IconButton>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: "24px" }}>
          <Stack gap={3}>
            <Stack gap={2}>
              <Grid container gap={2}>
                {isExcludedFromTrial ? (
                  <GateTrialExclusionChip />
                ) : (
                  <>
                    <IgChip
                      color="secondary"
                      label={name}
                      sx={{
                        ...sharedChipSx,
                        backgroundColor: IgBasePrimitives[50],
                        borderColor: IgBasePrimitives[50],
                        color: IgBasePrimitives[500],
                      }}
                    />
                    {discount && (
                      <IgChip
                        color="secondary"
                        label={`Save ${discount}%`}
                        sx={{
                          ...sharedChipSx,
                          backgroundColor: IgGreenPrimitives[200],
                          borderColor: IgGreenPrimitives[200],
                          color: IgGreenPrimitives["A300"],
                        }}
                      />
                    )}
                  </>
                )}
              </Grid>
              <IgTypography
                color={"primary"}
                fontSize={"16px"}
                fontWeight={600}
              >
                {title}
              </IgTypography>
              <IgTypography
                variant="body1"
                color={"textSecondary"}
                fontSize={"12px"}
                fontWeight={400}
                lineHeight={"150%"}
              >
                {description}
              </IgTypography>
            </Stack>

            <Grid container justifyContent={"center"}>
              <Box sx={PLAYER_FRAME_SX} ref={viewRef}>
                <Suspense
                  fallback={
                    <Skeleton
                      animation="wave"
                      variant="rectangular"
                      sx={PLAYER_FILL_SX}
                    />
                  }
                >
                  <MuxPlayer
                    accentColor={IgBasePrimitives[500]}
                    autoPlay
                    metadata={{ video_title: name }}
                    playbackId={videoPlaybackId}
                    style={PLAYER_FILL_STYLE}
                    title={name}
                  />
                </Suspense>
              </Box>
            </Grid>

            <Grid container gap={2} width={"100%"} justifyContent={"flex-end"}>
              <Button variant="outlined" onClick={onBack}>
                Back
              </Button>
              {buttons.map(({ label, onClick, sx, variant }, index) => (
                <Button
                  key={label + index}
                  variant={variant}
                  onClick={onClick}
                  sx={sx}
                >
                  {label}
                </Button>
              ))}
            </Grid>
          </Stack>
        </Box>
      </DialogContent>
    </>
  );
};
